/**
 * Klartext zu den Diensten des Stacks fuer „System & Betrieb“: Name und was der Dienst tut.
 * Schluessel = Compose-Dienstname (so melden ihn Health-Fanout und Container-Liste).
 */
export interface ServiceInfo {
  label: string;
  description: string;
}

const SERVICES: Record<string, ServiceInfo> = {
  // Eigene Dienste (mit Health-Pruefung)
  api: { label: 'API-Gateway', description: 'Zentrale Schnittstelle der Oberfläche; prüft die Anmeldung und leitet an die Fachdienste weiter.' },
  catalog: { label: 'Datenkatalog', description: 'Verzeichnis aller Datensätze mit Metadaten (DCAT-AP.de), Quellen, Suche und Katalog-Export.' },
  'data-gateway': { label: 'Datendienst', description: 'Liest und schreibt die Daten in den Speichern und liefert sie aus (REST, OGC, SensorThings, Dateien); prüft den Zugriff je Datensatz.' },
  ingestor: { label: 'Dateneingang', description: 'Holt Daten aus den Quellen ab (Abrufe nach Zeitplan, Dateien, MQTT-Ströme) und legt sie im Datendienst ab.' },
  derivation: { label: 'Ableitungen', description: 'Berechnet abgeleitete Datensätze (zusammengefasste oder verknüpfte Daten) aus vorhandenen Datensätzen.' },
  twin: { label: 'Digitaler Zwilling', description: 'Szenarien, Baukörper und Modelle des 3D-Zwillings; liefert die öffentliche Zwillingsansicht.' },
  alerting: { label: 'Alarme', description: 'Prüft Messwerte gegen Alarmregeln und meldet Überschreitungen.' },
  llm: { label: 'KI-Assistent', description: 'Verbindung zum eingestellten KI-Anbieter für Builder, Widgets und Erklärungen.' },
  sandbox: { label: 'Rechenumgebung', description: 'Führt Prognosen und Berechnungen abgeschottet aus: ohne Internet, mit schreibgeschütztem Dateisystem.' },
  // Infrastruktur (nur Container-Liste)
  frontend: { label: 'Oberfläche', description: 'Liefert die Web-Oberfläche aus.' },
  traefik: { label: 'Eingang (Reverse Proxy)', description: 'Nimmt alle Anfragen von außen an, verschlüsselt (TLS) und verteilt sie auf die Dienste.' },
  keycloak: { label: 'Anmeldung (Keycloak)', description: 'Konten, Rollen und Anmeldung für Oberfläche und Dienste.' },
  postgres: { label: 'Datenbank (PostgreSQL)', description: 'Speicher für Katalog, Zeitreihen (TimescaleDB), Geodaten (PostGIS) und 3D-Stadtmodell.' },
  objectstore: { label: 'Objektspeicher', description: 'Speicher für Dateien: Originale, Kacheln, Modelle, Berichte (S3-kompatibel).' },
  'objectstore-init': { label: 'Objektspeicher einrichten', description: 'Legt beim Start Buckets und Zugänge im Objektspeicher an und beendet sich dann.' },
  redis: { label: 'Zwischenspeicher (Redis)', description: 'Kurzlebige Daten und Sperren der Dienste.' },
  mosquitto: { label: 'Message-Broker (MQTT)', description: 'Nimmt Live-Daten von Geräten und Sensoren entgegen und verteilt sie.' },
  opensearch: { label: 'Suche (OpenSearch)', description: 'Volltext- und Bedeutungssuche im Datenkatalog.' },
  oxigraph: { label: 'Linked Data (Oxigraph)', description: 'Hält den Katalog als Linked Open Data für den SPARQL-Endpunkt.' },
  ollama: { label: 'Lokale KI-Modelle', description: 'Führt KI-Modelle auf dem eigenen Server aus (z. B. für die Suche).' },
  'twin-basedata': { label: 'Basisdaten Zwilling', description: 'Lädt Gelände, Gebäude, Starkregen und Bebauungspläne und bereitet sie für den Zwilling auf.' },
  'xplan-validator-api': { label: 'XPlanValidator', description: 'Amtliche Prüfung von Bebauungsplänen nach XPlanung.' },
  'xplan-validator-executor': { label: 'XPlanValidator (Prüflauf)', description: 'Führt die Prüfaufträge des XPlanValidators aus.' },
  'xplan-rabbitmq': { label: 'XPlanValidator (Warteschlange)', description: 'Warteschlange zwischen Prüfdienst und Prüflauf.' },
  'docker-proxy': { label: 'Container-Übersicht', description: 'Liefert diese Container-Liste; lässt nur Lesen zu, kein Starten oder Stoppen.' },
  'module-control': { label: 'Zusatzmodule', description: 'Startet und stoppt die Container eingeschalteter bzw. ausgeschalteter Zusatzmodule (z. B. LoRaWAN), sonst nichts.' },
  chirpstack: { label: 'LoRaWAN-Netzserver (ChirpStack)', description: 'Verwaltet LoRaWAN-Gateways und -Geräte und gibt deren Messwerte per MQTT weiter.' },
  'chirpstack-gateway-bridge': { label: 'LoRaWAN-Gateway-Brücke', description: 'Verbindet die LoRaWAN-Gateways mit ChirpStack.' },
};

export function serviceInfo(service: string): ServiceInfo | undefined {
  return SERVICES[service];
}

const UNITS: Record<string, [string, string]> = {
  second: ['Sekunde', 'Sekunden'],
  minute: ['Minute', 'Minuten'],
  hour: ['Stunde', 'Stunden'],
  day: ['Tag', 'Tagen'],
  week: ['Woche', 'Wochen'],
  month: ['Monat', 'Monaten'],
  year: ['Jahr', 'Jahren'],
};

/** Dauer aus dem Docker-Status („2 hours“, „About an hour“) im Dativ: „2 Stunden“, „etwa einer Stunde“. */
function dauer(en: string): string {
  const t = en.trim();
  if (/^less than a second$/iu.test(t)) return 'weniger als einer Sekunde';
  const about = /^about an? (second|minute|hour|day|week|month|year)$/iu.exec(t);
  if (about) return `etwa ${['hour', 'second', 'minute', 'week'].includes(about[1].toLowerCase()) ? 'einer' : 'einem'} ${UNITS[about[1].toLowerCase()][0]}`;
  const n = /^(\d+) (second|minute|hour|day|week|month|year)s?$/iu.exec(t);
  if (n) return `${n[1]} ${UNITS[n[2].toLowerCase()][n[1] === '1' ? 0 : 1]}`;
  return t;
}

/** Docker-Statustext auf Deutsch: „Up 31 hours (healthy)“ → „läuft seit 31 Stunden“. */
export function containerStatusDe(status: string): string {
  const up = /^Up (.+?)(?: \((?:healthy|unhealthy|health: starting)\))?$/u.exec(status);
  if (up) return `läuft seit ${dauer(up[1])}`;
  const exited = /^Exited \((\d+)\) (.+) ago$/u.exec(status);
  if (exited) return `beendet vor ${dauer(exited[2])} (Code ${exited[1]})`;
  const restarting = /^Restarting \((\d+)\) (.+) ago$/u.exec(status);
  if (restarting) return `startet neu (zuletzt vor ${dauer(restarting[2])}, Code ${restarting[1]})`;
  if (status === 'Created') return 'angelegt, nicht gestartet';
  return status;
}

/** Docker-Zustand auf Deutsch (State der Engine). */
export function containerStateDe(state: string): string {
  return { exited: 'beendet', created: 'angelegt', restarting: 'startet neu', paused: 'angehalten', dead: 'ausgefallen', removing: 'wird entfernt' }[state] ?? state;
}
